import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { apiGet, apiPost } from '@/api/client';
import type { CompanyMember, DepartmentMember, PlatformUser } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Button } from '@/app/components/ui/Button';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, FormActions, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useMe } from '@/features/me/MeContext';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

interface LinkCandidate {
  user_id: string;
  name: string;
  has_login: boolean;
}

function dedupe(candidates: LinkCandidate[]): LinkCandidate[] {
  const seen = new Map<string, LinkCandidate>();
  for (const c of candidates) seen.set(c.user_id, c);
  return [...seen.values()];
}

/**
 * The candidate pool for linking, matching what `POST /users/{id}/link`
 * actually lets each caller reach: a global admin may link any two
 * identities on the platform; a company or department admin only within a
 * scope they administer — so their pool is the union of every company and
 * department they hold, not any one of them.
 */
function useLinkCandidates() {
  const { permissions } = useMe();
  const { isAdmin, administeredCompanyIds, administeredDepartmentIds } = permissions;

  return useAsync(async () => {
    if (isAdmin) {
      const rows = (await apiGet('/users/all')) as unknown as PlatformUser[];
      return rows.map((u) => ({
        user_id: u.user_id,
        name: u.name ?? u.email ?? u.user_id,
        has_login: u.has_login,
      }));
    }

    const companyRows = await Promise.all(
      administeredCompanyIds.map(
        (companyId) =>
          apiGet('/companies/{companyId}/users', { params: { companyId } }) as unknown as Promise<
            CompanyMember[]
          >,
      ),
    );
    const departmentRows = await Promise.all(
      administeredDepartmentIds.map(
        (departmentId) =>
          apiGet('/departments/{departmentId}/users', {
            params: { departmentId },
          }) as unknown as Promise<DepartmentMember[]>,
      ),
    );

    return dedupe(
      [...companyRows.flat(), ...departmentRows.flat()].map((m) => ({
        user_id: m.user_id,
        name: m.name,
        has_login: m.has_login,
      })),
    );
  }, [isAdmin, administeredCompanyIds, administeredDepartmentIds]);
}

/**
 * Identity linking: an alias pointer, never a merge. Not scoped to any one
 * company or department — the caller's whole administered reach is the
 * candidate pool. The "at least one side login-less, the login becomes
 * primary" constraint is shown as a hint only — the API decides; re-
 * implementing the rule here would turn a courtesy into a second
 * authorization system.
 */
export function LinkIdentitiesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const candidates = useLinkCandidates();
  const [aliasId, setAliasId] = useState('');
  const [primaryId, setPrimaryId] = useState('');
  const form = useSubmit(async () => {
    await apiPost('/users/{userId}/link', {
      params: { userId: aliasId },
      body: { primary_user_id: primaryId },
    });
    await navigate('/manage/users');
  });

  const loginTag = (person: LinkCandidate) =>
    person.has_login ? t('manage.linkIdentities.hasLogin') : t('manage.linkIdentities.noLogin');

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('manage.linkIdentities.title')} lead={t('manage.linkIdentities.lead')} />

      <AsyncSection state={candidates} label={t('manage.linkIdentities.candidatesLabel')}>
        {(rows) =>
          rows.length < 2 ? (
            <EmptyState text={t('manage.linkIdentities.needTwo')} />
          ) : (
            <form
              className="flex max-w-2xl flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void form.submit();
              }}
            >
              <Field label={t('manage.linkIdentities.aliasLabel')}>
                <Select value={aliasId} onChange={(e) => setAliasId(e.target.value)} required>
                  <option value="">{t('manage.linkIdentities.choose')}</option>
                  {rows.map((person) => (
                    <option key={person.user_id} value={person.user_id}>
                      {person.name} {loginTag(person)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('manage.linkIdentities.primaryLabel')}>
                <Select value={primaryId} onChange={(e) => setPrimaryId(e.target.value)} required>
                  <option value="">{t('manage.linkIdentities.choose')}</option>
                  {rows
                    .filter((person) => person.user_id !== aliasId)
                    .map((person) => (
                      <option key={person.user_id} value={person.user_id}>
                        {person.name} {loginTag(person)}
                      </option>
                    ))}
                </Select>
              </Field>
              <SubmitError error={form.error} />
              <FormActions>
                <Button as={Link} to="/manage/users" variant="ghost">
                  {t('manage.linkIdentities.cancel')}
                </Button>
                <Button type="submit" disabled={form.pending || !aliasId || !primaryId}>
                  {t('manage.linkIdentities.link')}
                </Button>
              </FormActions>
            </form>
          )
        }
      </AsyncSection>
    </section>
  );
}
